import { PageSeo } from "@/components/PageSeo";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff } from "lucide-react";
const mbLearnLogo = '/mblearn-logo-HD.png';
import { signUpSchema, SignUpInput } from "@/lib/validations";
import { CURRENT_CONSENT_VERSION } from "@/lib/legal-versions";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { InstallPWAButton } from "@/components/InstallPWAButton";
import { SafariOnlyBanner } from "@/components/SafariOnlyBanner";
import { MassageBeachBackground } from "@/components/MassageBeachBackground";
import { AppleSignInButton } from "@/components/auth/AppleSignInButton";

const ConsentLabel = ({ t, language }: { t: (k: string) => string; language: string }) => {
  const raw = t("auth.consentCheckbox");
  const termsLabel = language === "en" ? "Terms of Service" : "Términos de Servicio";
  const privacyLabel = language === "en" ? "Privacy Policy" : "Política de Privacidad";

  const parts = raw.split(/\{terms\}|\{privacy\}/);
  const termsIdx = raw.indexOf("{terms}");
  const privacyIdx = raw.indexOf("{privacy}");

  const elements: React.ReactNode[] = [];
  let partIndex = 0;

  const ordered = [
    { idx: termsIdx, type: "terms" as const },
    { idx: privacyIdx, type: "privacy" as const },
  ].filter(o => o.idx >= 0).sort((a, b) => a.idx - b.idx);

  elements.push(<span key="p0">{parts[partIndex++]}</span>);
  for (const o of ordered) {
    if (o.type === "terms") {
      elements.push(
        <Link key="terms" to="/legal/terms" target="_blank" className="text-primary hover:underline font-medium">
          {termsLabel}
        </Link>
      );
    } else {
      elements.push(
        <Link key="privacy" to="/legal/privacy" target="_blank" className="text-primary hover:underline font-medium">
          {privacyLabel}
        </Link>
      );
    }
    elements.push(<span key={`p${partIndex}`}>{parts[partIndex++]}</span>);
  }

  return <span className="text-xs leading-relaxed text-muted-foreground">{elements}</span>;
};

const Register = () => {
  const { t, language } = useLanguage();
  const { user, session, loading: authLoading, signUp } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignUpInput>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Partial<SignUpInput>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user && session && user.email_confirmed_at) {
      console.log('🔄 [Register] User already authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [user, session, authLoading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: undefined });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setConsentError(false);

    if (!consentChecked) {
      setConsentError(true);
      setLoading(false);
      return;
    }

    const result = signUpSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<SignUpInput> = {};
      result.error.issues.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof SignUpInput] = err.message as any;
        }
      });
      setErrors(fieldErrors);
      setLoading(false);
      return;
    }

    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    const { error } = await signUp(formData.email, formData.password, fullName);
    
    if (error) {
      if (
        error.message?.includes("already registered") ||
        error.message?.includes("User already registered")
      ) {
        toast.error(t("auth.emailAlreadyRegistered"));
      } else {
        toast.error(error.message ? error.message : t("auth.errorCreating"));
      }
    } else {
      // Store pending consent to persist after email verification + first login.
      // TTL: 30 days. If the user never confirms email within that window we
      // discard the stored payload to avoid orphan personal data in
      // localStorage (GDPR/CCPA minimization). #7
      const now = Date.now();
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
      localStorage.setItem("mblearn_pending_consent", JSON.stringify({
        version: CURRENT_CONSENT_VERSION,
        language,
        type: "registration",
        timestamp: new Date(now).toISOString(),
        expiresAt: new Date(now + THIRTY_DAYS_MS).toISOString(),
      }));
    }
    
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen relative flex items-center justify-center p-4 overflow-x-hidden bg-background"
      style={{
        paddingTop: "max(1rem, env(safe-area-inset-top))",
        paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
      }}
    >

      <PageSeo
        path="/auth/register"
        title="Create your free MBLearn account — MBLEx Practice Tests"
        description="Sign up free for MBLearn and access 7 MBLEx study modules, 40,000+ practice questions and real-exam simulations."
        noindex
      />
      <SafariOnlyBanner />
      <MassageBeachBackground />
      
      <div className="mobile-container w-full max-w-[430px] p-4 sm:p-8 relative z-10 animate-fade-in">
        {/* PWA install oculto hasta publicación oficial en App Store y Play Store */}

        <div className="mb-4 text-center">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← {language === "es" ? "Volver al inicio" : "Back to home"}
          </Link>
        </div>
        <div className="text-center mb-8 animate-scale-in">
          <img src={mbLearnLogo} alt="MBLearn" className="w-28 h-28 rounded-2xl mx-auto mb-4 shadow-lg object-contain bg-transparent" />


          <h1 className="text-3xl font-bold text-foreground">
            {t("auth.createAccount")}
          </h1>
          <p className="text-muted-foreground mt-2">{t("auth.startPrep")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 bg-card/80 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-border animate-fade-in" style={{ animationDelay: "0.1s", animationFillMode: "backwards" }}>
          <div>
            <Label htmlFor="firstName" className="text-foreground">{t("auth.firstName")}</Label>
            <Input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              required
              className={`bg-background text-foreground border-input placeholder:text-muted-foreground ${errors.firstName ? "border-destructive" : ""}`}
            />
            {errors.firstName && (
              <p className="text-sm text-destructive mt-1">{errors.firstName}</p>
            )}
          </div>

          <div>
            <Label htmlFor="lastName" className="text-foreground">{t("auth.lastName")}</Label>
            <Input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              required
              className={`bg-background text-foreground border-input placeholder:text-muted-foreground ${errors.lastName ? "border-destructive" : ""}`}
            />
            {errors.lastName && (
              <p className="text-sm text-destructive mt-1">{errors.lastName}</p>
            )}
          </div>

          <div>
            <Label htmlFor="email" className="text-foreground">{t("auth.email")}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className={`bg-background text-foreground border-input placeholder:text-muted-foreground ${errors.email ? "border-destructive" : ""}`}
            />
            {errors.email && (
              <p className="text-sm text-destructive mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password" className="text-foreground">{t("auth.password")}</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                required
                className={`bg-background text-foreground border-input placeholder:text-muted-foreground pr-10 ${errors.password ? "border-destructive" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive mt-1">{errors.password}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {t("auth.passwordRequirements")}
            </p>
          </div>

          <div>
            <Label htmlFor="confirmPassword" className="text-foreground">{t("auth.confirmPassword")}</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className={`bg-background text-foreground border-input placeholder:text-muted-foreground pr-10 ${errors.confirmPassword ? "border-destructive" : ""}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showConfirmPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Consent checkbox */}
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={consentChecked}
                onCheckedChange={(checked) => {
                  setConsentChecked(checked === true);
                  if (checked) setConsentError(false);
                }}
                className={consentError ? "border-destructive" : ""}
              />
              <label htmlFor="consent" className="cursor-pointer leading-tight">
                <ConsentLabel t={t} language={language} />
              </label>
            </div>
            {consentError && (
              <p className="text-sm text-destructive">{t("auth.consentRequired")}</p>
            )}
          </div>

          {/* Notice at collection */}
          <p className="text-[10px] leading-relaxed text-muted-foreground/70 border-t border-border pt-3">
            {t("auth.noticeAtCollection")}
          </p>

          <Button
            type="submit"
            className="w-full py-6 bg-gradient-to-r from-primary to-accent text-primary-foreground"
            disabled={loading}
            size="lg"
          >
            {loading ? t("auth.creatingAccount") : t("auth.register")}
          </Button>
        </form>

        {/* Apple Sign In oculto temporalmente — ver ticket Apple BYOC */}
        {false && (
          <div className="mt-4 animate-fade-in" style={{ animationDelay: "0.15s", animationFillMode: "backwards" }}>
            <div className="relative flex items-center justify-center mb-4">
              <div className="border-t border-border flex-grow" />
              <span className="mx-3 text-xs text-muted-foreground uppercase">{t("auth.orContinueWith")}</span>
              <div className="border-t border-border flex-grow" />
            </div>
            <AppleSignInButton />
          </div>
        )}

        <div className="mt-6 text-center text-sm animate-fade-in" style={{ animationDelay: "0.2s", animationFillMode: "backwards" }}>
          <p className="text-muted-foreground">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link to="/auth/login" className="text-primary hover:underline font-medium">
              {t("auth.loginHere")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
